<template>
  <div class="timeline-list-item">
    <BoardListCard
      :key="cardId"
      :card-id="cardId"
      :list-id="listId"
      :index="index"
      :large-board="largeBoard"
      :timeline-view="true"
      :list-cards="listCards"
      class="timeline-sidebar-item"
      :style="{
        width: `${timelineSidebarWidth - 1}px`,
      }"
      :read-only="readOnly"
    />
    <div class="timeline-item-container">
      <div class="timeline-item-wrap">
        <div
          class="timeline-item-wrap-position"
          :style="{
            transform: `translateX(${-position}px)`,
          }"
        >
          <div
            v-if="showTimelineCard"
            class="cursor-default"
          >
            <div
              v-if="showArrowButton"
              v-tooltip="{
                content: translate('goToCard'),
                placement: 'bottom',
              }"
              :style="arrowButton.style"
              class="go-to-card-icon-wrap pt"
              @click="goToCard"
            >
              <em
                :style="arrowButton.icon"
                class="custom-icons go-to-card-icon"
              />
            </div>
            <TimelineListCard
              v-if="showTimelineCard"
              :card-id="cardId"
              :left-position="leftPosition"
              :right-position="rightPosition"
              :width="cardWidth"
              :is-start-date-set="isStartDateSet"
              :is-end-date-set="isEndDateSet"
              :is-over-due="isCardOverdue"
              :end-date-is-before-start-date="endDateIsBeforeStartDate"
              :start-date="startDate"
              :end-date="endDate"
              :position="position"
              :container-width="containerWidth"
              :list-cards="listCards"
            />
          </div>
          <div
            v-if="!showTimelineCard"
            :style="{
              transform: `translateX(${position}px)`,
            }"
            class="hidden-item-wrap-position"
            @mouseenter="handleMouseEnterEvent"
            @mousemove="handleMouseMoveEvent"
            @mouseleave="handleMouseLeaveEvent"
          >
            <TimelineShowCardButton
              v-if="showAddHiddenCardButton && !readOnly"
              :left-position="addHiddenCardButtonPosition"
              @showCard="showCard"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BoardListCard from '@/components/widgets/BoardListCard';
import TimelineListCard from '@/components/widgets/TimelineListCard';
import TimelineShowCardButton from '@/components/widgets/TimelineShowCardButton';
import { getSidebarWidth } from '@/utilities/rootStyles';
import timelineMixin from '@/mixins/timelineMixin';
import dueDateMixin from '@/mixins/dueDateMixin';
import cardStatuses from '@/mixins/cardStatuses';
import customIconsMixin from '@/mixins/customIconsMixin';
import getTime from 'date-fns/getTime';
import addDays from 'date-fns/addDays';
import compareAsc from 'date-fns/compareAsc';
import EventBus from '@/utilities/eventBus';
import roundUpToNearestMultiple from '@/utilities/roundUpToNearestMultiple';
import { computed } from 'vue';
import useBoardEmbed from '@/boards/composables/useBoardEmbed';
import { ResourceType } from '@/types/resources';
import useCurrentRole from '@/utilities/composables/useCurrentRole';

export default {
  name: 'TimelineListItem',
  components: {
    BoardListCard,
    TimelineListCard,
    TimelineShowCardButton,
  },
  mixins: [timelineMixin, dueDateMixin, cardStatuses, customIconsMixin],
  props: {
    cardId: {
      type: String,
      required: true,
    },
    largeBoard: {
      type: Boolean,
      required: false,
      default: false,
    },
    index: {
      type: Number,
      required: true,
      default: 0,
    },
    containerWidth: {
      type: Number,
      required: true,
    },
    position: {
      type: Number,
      required: true,
    },
    listId: {
      type: String,
      required: false,
      default: '',
    },
    listCards: {
      type: Array,
      required: false,
      default: () => [],
    },
  },

  setup() {
    const { embeddedBoardAttributes, embed, inEpicBoard } = useBoardEmbed();
    const { isGuestReader } = useCurrentRole();

    const readOnly = computed(
      () => embeddedBoardAttributes.value?.readOnly || isGuestReader.value || false
    );

    return {
      readOnly,
      embeddedBoardAttributes,
      embed,
      inEpicBoard,
    };
  },

  data() {
    return {
      addHiddenCardButtonPosition: 0,
      showAddHiddenCardButton: false,
    };
  },
  computed: {
    timelineSidebarWidth() {
      return this.$store.getters.getTimelineSidebarWidth;
    },
    positionAsDate() {
      const positionInTimeline =
        this.position - (this.containerWidth / 2 - this.addHiddenCardButtonPosition);
      return this.positionToDate(positionInTimeline);
    },
    card() {
      return this.$store.getters.getCards[this.cardId];
    },
    isStartDateSet() {
      return !!this.card.start_date;
    },
    isEndDateSet() {
      return !!this.card.due_date;
    },
    showTimelineCard() {
      return this.isStartDateSet || this.isEndDateSet;
    },
    bothDatesSet() {
      return this.isStartDateSet && this.isEndDateSet;
    },
    dayWidthOffset() {
      return this.bothDatesSet ? this.dayWidth : 0;
    },
    startDate() {
      return this.isStartDateSet
        ? this.card.start_date * 1000
        : getTime(
            addDays(this.card.due_date * 1000, -Math.floor(this.cardWidthOffset / this.dayWidth))
          );
    },
    endDate() {
      if (this.startedWithoutEndDate) return getTime(this.today.date);
      return this.isEndDateSet && !this.endDateIsBeforeStartDate
        ? this.card.due_date * 1000
        : getTime(
            addDays(this.card.start_date * 1000, Math.floor(this.cardWidthOffset / this.dayWidth))
          );
    },
    leftPosition() {
      return this.positionToLeft(this.dateToPosition(this.startDate));
    },
    endDatePosition() {
      return this.dateToPosition(this.endDate);
    },
    rightPosition() {
      return this.positionToRight(
        this.startedWithoutEndDate ? this.today.position : this.endDatePosition
      );
    },
    cardWidthOffset() {
      return this.bothDatesSet && !this.endDateIsBeforeStartDate
        ? 0
        : this.getWidthOfElement(this.card.title, 'div');
    },
    cardWidth() {
      return (this.startedWithoutEndDate || this.bothDatesSet) && !this.endDateIsBeforeStartDate
        ? this.containerWidth - this.leftPosition - this.rightPosition + this.dayWidthOffset
        : 0;
    },
    startedWithoutEndDate() {
      return this.card.status === this.cardStatuses.started.id && !this.isEndDateSet;
    },
    showLeftArrowButton() {
      return this.rightPosition + this.position - this.dayWidthOffset > this.containerWidth;
    },
    showRightArrowButton() {
      return this.leftPosition - this.position > this.containerWidth;
    },
    arrowButton() {
      const { position } = this;
      let side = 'left';
      if (!this.showLeftArrowButton) {
        if (!this.showRightArrowButton) return null;
        side = 'right';
        return {
          position,
          side,
          icon: this.getArrowIcon(side),
          style: {
            right: `${this.$constants.timelineArrowButtonOffset - position}px`,
          },
        };
      }
      return {
        position,
        side,
        icon: this.getArrowIcon(side),
        style: {
          left: `${position + this.$constants.timelineArrowButtonOffset}px`,
        },
      };
    },
    showArrowButton() {
      return this.arrowButton && this.showTimelineCard;
    },
    isCardOverdue() {
      return this.bothDatesSet && this.today.position > this.endDatePosition;
    },
    endDateIsBeforeStartDate() {
      return this.bothDatesSet && compareAsc(this.card.due_date, this.card.start_date) === -1;
    },

    sprintRoute() {
      return this.$route === ResourceType.Sprint;
    },
  },
  methods: {
    goToCard() {
      EventBus.$emit('goToCard', this.leftPosition - 20);
    },
    handleMouseEnterEvent(event) {
      if (this.draggingIsOn) return;
      this.showAddHiddenCardButton = true;
      this.updateButtonPosition(event.clientX);
    },
    handleMouseMoveEvent(event) {
      this.updateButtonPosition(event.clientX);
    },
    updateButtonPosition(clientX) {
      let leftOffset = getSidebarWidth();
      if (
        this.embed &&
        this.embeddedBoardAttributes.boardType !== 'sprint' &&
        !this.sprintRoute &&
        !this.inEpicBoard
      ) {
        const position = this.embeddedBoardAttributes.getPos();
        const domNode = this.embeddedBoardAttributes.editor.view.nodeDOM(position);
        leftOffset = domNode.getBoundingClientRect().left;
      }
      this.addHiddenCardButtonPosition = clientX - leftOffset - this.timelineSidebarWidth;
    },
    handleMouseLeaveEvent() {
      this.showAddHiddenCardButton = false;
    },
    showCard() {
      const dateTimestamp = getTime(this.positionAsDate) / 1000;
      this.setCardDate(dateTimestamp, false, 'start_date', this.cardId);
    },
    getArrowIcon(side) {
      return this.getCustomIconStyle(this.iconPath(`arrow-${side}`));
    },
    getCardWidth(leftPosition, rightPosition, dayWidth) {
      return this.containerWidth - leftPosition - rightPosition + dayWidth;
    },
    getWidthOfElement(content) {
      const helplerElement = document.getElementById(this.$constants.timelineHelperElementId);
      helplerElement.innerText = content;
      const { width } = helplerElement.getBoundingClientRect();
      const roundUpWidth = roundUpToNearestMultiple(width, this.dayWidth);
      helplerElement.innerText = '';
      return roundUpWidth;
    },
  },
};
</script>

<style lang="scss">
$infoIconSize: 16px;
$listItemBaseHeight: 40px;
$listItemBorderWidth: 1px;
$listItemHeight: calc($listItemBaseHeight + $listItemBorderWidth);
$cardItemHeight: 32px;
$cardItemTopOffset: calc((($listItemBaseHeight - $cardItemHeight)) / 2);
$resizerWidth: 30px;
$resizerOffset: calc($timelineCardTitlePaddingX - $resizerWidth);
$resizerAnimateDestination: 4px;
$resizerCardGap: 4px;
$resizerPosition: calc($resizerAnimateDestination + $timelineCardTitlePaddingX + $resizerCardGap);

.timeline-list-item {
  display: flex;

  &:hover {
    .board-list-card-wrap {
      @apply bg-emphasised-hover;
    }
    .timeline-item-container {
      @apply bg-hover-defaultTransparent;

      .timeline-item-wrap {
        .timeline-item-wrap-position {
          .hidden-item-wrap-position {
            .timeline-show-card-button {
              opacity: 1;
            }
          }
        }
      }
    }
  }

  .timeline-item-container {
    flex: 1 1 auto;
    display: flex;
    position: relative;

    .timeline-item-wrap {
      flex: 1 1 auto;
      position: relative;
      overflow: hidden;

      .timeline-item-wrap-position {
        position: absolute;
        inset: 0;

        .go-to-card-icon-wrap {
          @apply absolute
            flex
            items-center
            justify-center
            w-6
            h-6
            bg-icon-bg
            whitespace-nowrap
            top-2/4
            -translate-y-1/2
            rounded
            border
            border-solid
            border-icon-bg;

          .custom-icons {
            @apply bg-icon-default;
          }

          &:hover {
            @apply border-important-border;

            .custom-icons {
              @apply bg-icon-hover;
            }
          }
        }

        .timeline-item-card {
          border: 1px solid;
          // changed from block to flex - check after
          @apply flex rounded-md border-emphasised-border bg-emphasised-bg;
          height: $cardItemHeight;
          box-shadow: var(--timelineCardShadow) 0px 1px 4px;

          &.position {
            position: absolute;
            top: $cardItemTopOffset;
          }

          &.no-start-date {
            border-radius: 0 6px 6px 0;
            border-color: transparent;
            background-color: transparent;
            background-clip: padding-box;
            overflow: visible;

            &::before {
              @apply absolute bg-emphasised-bg border border-solid border-emphasised-border inset-0 content-[''];
              mask-image: linear-gradient(to left, rgb(0, 0, 0), rgba(0, 0, 0, 0));
              z-index: -1;
              margin: -1px 0 -1px -1px;
              border-radius: inherit;
            }
          }

          &.no-end-date,
          &.end-date-before-start-date {
            border-radius: 6px 0 0 6px;
            border-color: transparent;
            background-color: transparent;
            background-clip: padding-box;
            overflow: visible;

            &::before {
              @apply absolute bg-emphasised-bg border border-solid border-emphasised-border inset-0 content-[''];
              mask-image: linear-gradient(to right, rgb(0, 0, 0), rgba(0, 0, 0, 0));
              z-index: -1;
              margin: -1px -1px -1px 0px;
              border-radius: inherit;
            }
          }

          &.end-date-before-start-date {
            &::before {
              border-color: $warning400;
            }
          }

          &.over-due {
            border: 1px solid $error400;
          }

          .timeline-card-resizer {
            position: absolute;
            width: $resizerWidth;
            top: 0px;
            bottom: 0px;
            cursor: col-resize;

            &::after {
              opacity: 0;
              height: 26px;
              content: ' ';
              position: absolute;
              top: 3px;
              border-radius: 2px;
              width: 2px;
              transition:
                transform 0.2s ease 0s,
                opacity 0.2s ease 0s;
              background-color: rgb(223, 225, 228);
            }
            &.left {
              left: $resizerOffset;

              &::after {
                right: $resizerPosition;
                transform: translateX(4px);
              }
            }

            &.right {
              right: $resizerOffset;

              &::after {
                left: $resizerPosition;
                transform: translateX(-4px);
              }
            }
            &:hover:not(.disabled),
            &.active {
              &::after {
                opacity: 1;
                transform: translateX(0px);
              }
            }
          }

          .item-card-content-wrap {
            @apply flex items-center overflow-visible w-full;

            .item-card-title-wrap {
              @apply flex items-center;
            }
          }

          .timeline-info-icon {
            position: absolute;
            left: calc($resizerOffset - $infoIconSize);
            height: inherit;
            display: flex;
            align-items: center;

            .custom-icons {
              width: $infoIconSize;
              height: $infoIconSize;
            }
          }
        }

        .hidden-item-wrap-position {
          position: absolute;
          inset: 0;
          height: $listItemHeight;
        }
      }
    }
  }

  .timeline-sidebar-item {
    container-name: timeline-sidebar-item;
    container-type: inline-size;
  }
}
.item-card-title,
#timeline-helper-element {
  @apply truncate text-sm text-text-default;
  backface-visibility: hidden;
  -moz-backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  -ms-backface-visibility: hidden;
}
.item-card-title-wrap,
#timeline-helper-element {
  padding: 0 $timelineCardTitlePaddingX;
}

.cards-layout--timeline {
  .board-grid-view-main-wrap {
    .board-view-wrap {
      .timeline-sidebar-item {
        .board-list-card-wrap {
          .list-card {
            @apply pr-2;

            .preview-card-footer {
              @apply pl-9;
              .card-icons-col {
                .tags-item,
                .priority-icon,
                .estimate-icon {
                  visibility: hidden !important;
                  width: 0 !important;
                  padding: 0 !important;
                }

                .members-item {
                  width: fit-content !important;

                  .members-wrap {
                    .rest-member-wrap {
                      margin-right: -2px !important;
                    }
                  }
                }

                .add-members-wrap {
                  .add-members-button {
                    margin-right: -2px;
                  }
                }
              }
            }
          }

          &.hover {
            .list-card {
              .preview-card-footer {
                .card-icons-col {
                  .tags-item {
                    span:not(.custom-section-value) {
                      visibility: hidden !important;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}

// @container timeline-sidebar-item (width < 360px) {
// .board-list-card-wrap {
// add rules for cases when this is smaller than 360px
// }
// }
</style>
